'use client';

import { Card, Heading, Icon } from '@mv/web-ui';
import type { ReactNode } from 'react';
import { useDataTags } from '@/modules/tracking/analytics/useDataTags';
import { assertPayloadType } from '@/modules/utils/assert';
import { CallToActionGroup } from '@/payload/components/CallToActionGroup/CallToActionGroup';
import type { Illustration, CardBlock as Props } from '@/payload/types';

type CardBlockProps = Omit<Props, 'content'> & {
	children: ReactNode;
};

export default function CardBlock({ blockType, id, header, footer, tag, icon, children }: CardBlockProps) {
	const { blockRef, labelRef, contentRef } = useDataTags(blockType, id);

	return (
		<Card data-component="CardBlock" ref={blockRef}>
			{icon?.illustration && <CardIllustration illustration={icon.illustration} />}

			{tag?.text && (
				<Card.Tag
					size={tag?.options?.size ?? 'small'}
					appearance={tag?.options?.appearance ?? 'blue'}
					alignment={tag.options?.alignment ?? 'left'}
				>
					{tag.text}
				</Card.Tag>
			)}

			<Card.Content>
				{header?.heading && (
					<Card.Header>
						<Heading ref={labelRef} tag="h3" styleAs="h4" textAlignment={header?.alignment ?? 'left'}>
							{header?.heading}
						</Heading>
					</Card.Header>
				)}

				{children && <Card.Body ref={contentRef}>{children}</Card.Body>}

				{footer?.content && footer.content.length > 0 && (
					<Card.Footer justify={footer.alignment ?? 'left'}>
						<CallToActionGroup links={footer.content} size={footer.link_size} />
					</Card.Footer>
				)}
			</Card.Content>
		</Card>
	);
}

function CardIllustration({ illustration }: { illustration: Illustration | number }) {
	assertPayloadType<Illustration>(illustration);

	return (
		<Card.Icon>
			<Icon iconUrl={illustration.url} size="400" />
		</Card.Icon>
	);
}
